import { ApplicationStatus } from "model/Application";
import type { TFunction } from "i18next";
import type { PendingApplicationAction } from "./teamPublicDetailsModal.contract";

interface ApplicationActionModalCopy {
  title: string;
  body: string;
  confirmLabel: string;
}

interface ResolveApplicationActionModalCopyParams {
  action: PendingApplicationAction;
  t: TFunction;
  language: string;
}

const isSwedishLanguage = (language: string): boolean => {
  const normalizedLanguage = language.toLocaleLowerCase();
  return normalizedLanguage === "se" || normalizedLanguage.startsWith("sv");
};

export const formatApplicantPossessiveName = ({
  language,
  name,
}: {
  language: string;
  name: string;
}): string => {
  const trimmedName = name.trim();
  if (trimmedName.length === 0) return trimmedName;

  if (isSwedishLanguage(language)) {
    return /[sxz]$/i.test(trimmedName) ? trimmedName : `${trimmedName}s`;
  }

  return /s$/i.test(trimmedName) ? `${trimmedName}'` : `${trimmedName}'s`;
};

/**
 * Resolves localized modal copy for accept/decline actions on a pending application.
 */
export const resolveApplicationActionModalCopy = (
  params: ResolveApplicationActionModalCopyParams,
): ApplicationActionModalCopy => {
  const applicantPossessiveName = formatApplicantPossessiveName({
    language: params.language,
    name: params.action.applicantName,
  });

  if (params.action.status === ApplicationStatus.OFFERED) {
    return {
      title: params.t("Schedule.TeamPublicConfirmAcceptTitle"),
      body: params.t("Schedule.TeamPublicConfirmAcceptBody", {
        applicantPossessiveName,
      }),
      confirmLabel: params.t("Schedule.TeamPublicAccept"),
    };
  }

  return {
    title: params.t("Schedule.TeamPublicConfirmDeclineTitle"),
    body: params.t("Schedule.TeamPublicConfirmDeclineBody", {
      applicantPossessiveName,
    }),
    confirmLabel: params.t("Schedule.TeamPublicDecline"),
  };
};
